import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { Button } from '/imports/ui/Button';
import { Icon } from '/imports/ui/Icon';
import { Modal } from '/imports/ui/Modal';
import { Table } from '/imports/ui/Table';
import { NumberInput } from '/imports/ui/NumberInput';
import { RichSelect } from '/imports/ui/RichSelect';
import { useMethod } from '/imports/hooks';
import { mapById, randomString } from '/imports/utils';
import { css } from '/imports/ui/css';
import { Interconnection, modelClass as interconnectionModelClass } from '/imports/api/interconnections';
import { ProductionMeter, modelClass as productionMeterModelClass } from '/imports/api/productionMeters';

const InterconnectionsModalStyled = styled(Modal)`
  .modal-window {
    width: 45rem;
    max-width: 100%;
  }
  .add-row {
    display: flex;
    gap: 0.5rem;
    align-items: flex-end;
    margin-top: 1rem;
    .production-meter-select {
      flex: 1;
    }
    label {
      display: block;
    }
  }
  table {
    width: 100%;
    td:last-child, th:last-child {
      text-align: right;
    }
    .delete-icon {
      cursor: pointer;
      color: ${css.red2};
    }
  }
`;

/**
 * Modal for linking production meters to a consumption meter with weights.
 */
export const InterconnectionsModal = (props: { consumptionMeterId: string, onClose: () => void }) => {
  const { consumptionMeterId, onClose } = props;
  const [refreshKey, setRefreshKey] = useState('');
  const [newProductionMeterId, setNewProductionMeterId] = useState<string>();
  const [newWeight, setNewWeight] = useState<number | undefined>(1);
  const [saving, setSaving] = useState(false);
  const [interconnectionsResult] = useMethod(interconnectionModelClass.listMethod, { query: { consumptionMeterId } } as any, [consumptionMeterId, refreshKey]);
  const [productionMetersResult] = useMethod(productionMeterModelClass.listMethod, {}, []);
  const interconnections: Interconnection[] = interconnectionsResult?.documents || [];
  const allProductionMeters: ProductionMeter[] = productionMetersResult?.documents || [];
  const productionMeterMap = mapById(allProductionMeters);
  const connectedIds = new Set(interconnections.map((i) => i.productionMeterId));
  const availableMeters = allProductionMeters.filter((m) => !connectedIds.has(m._id));
  /**
   * Creates an interconnection from the selected production meter and weight.
   */
  const addInterconnection = async () => {
    if (!newProductionMeterId || newWeight === undefined) return;
    setSaving(true);
    try {
      await interconnectionModelClass.createMethod.call({ consumptionMeterId, productionMeterId: newProductionMeterId, weight: newWeight });
      setNewProductionMeterId(undefined);
      setNewWeight(1);
      setRefreshKey(randomString());
    }
    catch (error: any) {
      toast.error(error.message || error.reason || `${error}`);
    }
    finally {
      setSaving(false);
    }
  };
  /**
   * Deletes an interconnection and refreshes the list.
   * @param interconnectionId - id of the interconnection to remove
   */
  const removeInterconnection = async (interconnectionId: string) => {
    try {
      await interconnectionModelClass.deleteMethod.call(interconnectionId);
      setRefreshKey(randomString());
    }
    catch (error: any) {
      toast.error(error.message || error.reason || `${error}`);
    }
  };
  return (
    <InterconnectionsModalStyled title={strings.ConfigureInterconnections()} onClose={onClose} body={
      <>
        {
          interconnections.length ?
            <Table>
              <table>
                <thead>
                  <tr>
                    <th>{strings.ProductionMeter()}</th>
                    <th>{strings.Weight()}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {interconnections.map((ic) => (
                    <tr key={ic._id}>
                      <td>{productionMeterMap[ic.productionMeterId]?.name || ic.productionMeterId}</td>
                      <td>{ic.weight}</td>
                      <td><Icon className="delete-icon" icon="close" onClick={() => removeInterconnection(ic._id)} tip={strings.Remove()} /></td>
                    </tr>
                  ))}
                  {!interconnections.length && <tr><td colSpan={3}>{strings.NoInterconnections()}</td></tr>}
                </tbody>
              </table>
            </Table>
            :
            null
        }
        <div className="add-row" key={refreshKey}>
          <div className="production-meter-select">
            <label>{strings.Meter()}</label>
            <RichSelect
              placeholder={strings.ProductionMeter()}
              value={newProductionMeterId}
              options={availableMeters.map((m) => ({ value: m._id, label: m.name || m._id }))}
              onChange={(option) => setNewProductionMeterId(option.value)}
              fixedOptions={true}
            />
          </div>
          <div>
            <label>{strings.Weight()}</label>
            <NumberInput
              placeholder={strings.Weight()}
              value={newWeight}
              onChange={(weight) => setNewWeight(weight)}
              min="0"
              max="1"
              step="0.01"
            />
          </div>
          <Button onClick={addInterconnection} disabled={saving || !newProductionMeterId || newWeight === undefined}>{strings.Add()}</Button>
        </div>
      </>
    } />
  );
};
